import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface AuditEntry {
  id: string;
  action_type: string;
  entity_type: string;
  entity_label?: string;
  performed_by_name?: string;
  created_at: string;
}

interface Props {
  auditLog: AuditEntry[];
}

function formatTime(iso: string) {
  try {
    return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '—';
  }
}

function getCenterFromEntityType(entityType: string): { label: string; color: string } {
  const t = (entityType || '').toLowerCase();
  if (t.includes('employee') || t.includes('hr') || t.includes('candidate') || t.includes('onboard')) {
    return { label: 'HR', color: 'var(--primary)' };
  }
  if (t.includes('payroll')) {
    return { label: 'PAYROLL', color: 'var(--color-amber)' };
  }
  if (t.includes('shift') || t.includes('schedule')) {
    return { label: 'SHIFT', color: 'var(--color-purple)' };
  }
  if (t.includes('sentinel') || t.includes('role') || t.includes('permission')) {
    return { label: 'SENTINEL', color: 'var(--color-green)' };
  }
  return { label: 'SYS', color: 'var(--muted-foreground)' };
}

function formatActionLabel(actionType: string): string {
  return (actionType || 'Action')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function ActivityFeedColumn({ auditLog }: Props) {
  return (
    <div className="card-elevated rounded-xl p-4 h-full">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-card-foreground">Recent Activity</h3>
        <span className="text-xs text-muted-foreground">Audit log · Today</span>
      </div>

      {auditLog.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Icon name="ClipboardDocumentListIcon" size={28} className="text-muted-foreground mb-2" />
          <p className="text-sm font-medium text-muted-foreground">No activity yet</p>
          <p className="text-xs text-muted-foreground mt-1">Actions taken in HR, Payroll and other centres will appear here</p>
        </div>
      ) : (
        <div className="space-y-0">
          {auditLog.map((act, i) => {
            const center = getCenterFromEntityType(act.entity_type);
            return (
              <div key={act.id} className="flex gap-3 py-2.5 border-b border-border last:border-0 hover:bg-muted/30 px-2 -mx-2 rounded-md transition-colors cursor-pointer">
                <div className="flex flex-col items-center flex-shrink-0 pt-0.5">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: center.color }}></span>
                  {i < auditLog.length - 1 && (
                    <span className="w-px flex-1 mt-1" style={{ background: 'var(--border)', minHeight: '20px', display: 'block' }}></span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-semibold px-1.5 py-0.5 rounded font-mono"
                      style={{ background: 'rgba(0,0,0,0.3)', color: center.color, border: `1px solid ${center.color}33` }}>
                      {center.label}
                    </span>
                    <span className="text-xs font-medium text-card-foreground">{formatActionLabel(act.action_type)}</span>
                    <span className="text-xs text-muted-foreground ml-auto">{formatTime(act.created_at)}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">
                    {act.performed_by_name ? `${act.performed_by_name} · ` : ''}
                    {act.entity_label || act.entity_type || ''}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}